import { trackOutputAction } from 'analytics';
import forge from 'apis/forge';
import Icon, { IconType } from 'components/Icon';
import { useCallback, useState } from 'react';
import { shallowEqual, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { selectActiveScene, selectFrame, selectUser } from 'redux/db/reducers';
import { RootReducer } from 'redux/reducers';
import { useTheme } from 'styled-components';
import { unthemed } from 'tokens';
import { frameShareUrl } from 'utils';
import Modal from '../..';
import * as ModalStyles from '../../styles';
import SceneUserManagerModal from '../SceneUserManagerModal';
import * as S from '../styles';

interface ShareFrameModalProps {
	frameId: string;
	onClose: () => void;
}

const ShareFrameModal = ({ frameId, onClose }: ShareFrameModalProps) => {
	const theme = useTheme();
	const [showManageEditors, setShowManageEditors] = useState(false);

	const { frame, scene, user } = useSelector((r: RootReducer) => {
		return {
			frame: selectFrame(r.db, frameId),
			scene: selectActiveScene(r.db, r.mode.activeSceneId),
			user: selectUser(r.db),
		};
	}, shallowEqual);
	const [publiclyReadable, setPubliclyReadable] = useState(!!frame?.metadata?.publiclyReadable);

	// Determine the current access setting

	//------------------------------------------------------------------------------------------------
	// HOOKS
	//------------------------------------------------------------------------------------------------

	//------------------------------------------------------------------------------------------------
	// HANDLERS
	//------------------------------------------------------------------------------------------------

	const close = useCallback(() => {
		onClose();
	}, [onClose]);

	const handleAccessChange = async (newPubliclyReadable: boolean) => {
		if (!frame) {
			toast.error('Internal error: frame not found');
			return;
		}

		if (frame.viewOnly) {
			toast.warning('You cannot change sharing settings in view-only mode');
			return;
		}

		try {
			forge.updateFrame(
				{
					update: {
						id: frameId,
						metadata: {
							...frame.metadata,
							publiclyReadable: newPubliclyReadable,
						},
					},
				},
				true
			);
			toast.success('Prototype access updated');
			setPubliclyReadable(newPubliclyReadable);
		} catch (error) {
			toast.error('Failed to update prototype access settings');
			console.error('Error updating frame access:', error);
		}
	};

	//------------------------------------------------------------------------------------------------
	// RENDERING
	//------------------------------------------------------------------------------------------------
	const renderAccessControl = () => {
		return (
			<S.AccessSection>
				<ModalStyles.SectionTitle>Access Control</ModalStyles.SectionTitle>
				<S.ToggleContainer>
					{!frame?.viewOnly && (
						<S.ToggleButton selected={!publiclyReadable} onClick={() => handleAccessChange(false)}>
							<S.ToggleButtonContent>
								<S.ToggleButtonIcon>
									<Icon type={IconType.LOCK} color={theme.gray[1000]} width={16} height={16} />
								</S.ToggleButtonIcon>
								<S.ToggleButtonText>Only project editors</S.ToggleButtonText>
							</S.ToggleButtonContent>
						</S.ToggleButton>
					)}
					<S.ToggleButton selected={publiclyReadable} onClick={() => handleAccessChange(true)}>
						<S.ToggleButtonContent>
							<S.ToggleButtonIcon>
								<Icon type={IconType.GLOBE} color={theme.gray[1000]} width={16} height={16} />
							</S.ToggleButtonIcon>
							<S.ToggleButtonText>Anyone with the link can view</S.ToggleButtonText>
						</S.ToggleButtonContent>
					</S.ToggleButton>
				</S.ToggleContainer>
				{!frame?.viewOnly && (
					<S.ManageLink
						onClick={() => {
							setShowManageEditors(true);
						}}
					>
						Manage Project Editors →
					</S.ManageLink>
				)}
			</S.AccessSection>
		);
	};

	if (scene && showManageEditors) {
		return (
			<SceneUserManagerModal
				scene={scene}
				onBack={() => setShowManageEditors(false)}
				onClose={close}
			/>
		);
	}

	return (
		<Modal title={'Share this prototype'} cancelLabel="Close" submitLabel="Save" onClose={close}>
			<>
				{renderAccessControl()}
				<S.LinkSection>
					<S.LinkButton
						onClick={() => {
							if (!frame) {
								toast.error('Internal error: frame not found');
								return;
							}
							trackOutputAction('copy_frame_link');
							navigator.clipboard.writeText(frameShareUrl(frame));
							toast.success('Link copied');
						}}
					>
						<Icon type={IconType.COPY} color={unthemed.gray['0']} width={14} height={14} />
						<S.Link>Copy prototype link</S.Link>
					</S.LinkButton>
				</S.LinkSection>
			</>
		</Modal>
	);
};

export default ShareFrameModal;
